import {
  HOURS_PER_DAY,
  INSTALLMENT_BILLING,
  RECURRING_BILLING,
  WORKING_DAYS_PER_MONTH,
} from '../../constants';
import { hourlySoldHoursPerMonth, lineBudgetMax } from '../../utils/budget';
import { contractTotalMonths } from '../../utils/dates';
import { uid } from '../../utils/format';

const filled = (v) => v != null && v !== '';
const text = (v) => (filled(v) ? String(v) : '');

// Le jours vendus (ferme) prime sur les jours min/max (estimés) pour préremplir "Jours total" :
// champ dédié si présent (forfait_global/régie), sinon budget vendu ÷ TJM pour le forfait par
// brique (pas de champ jours_vendus dédié pour ce type — cf. ligne "Budget vendu" du CRM).
function joursVendus(l, factL) {
  if (filled(l.jours_vendus))
    return l.unite_vendu === 'heures'
      ? parseFloat(l.jours_vendus) / HOURS_PER_DAY
      : parseFloat(l.jours_vendus);
  const tjmL = parseFloat(l.tjm_vendu) || parseFloat(l.tjm) || 0;
  const soldBudget = parseFloat(l.budget_vendu);
  return factL === 'forfait_brique' && tjmL > 0 && !isNaN(soldBudget) ? soldBudget / tjmL : null;
}

function joursTotal(l, factL) {
  const soldDays = joursVendus(l, factL);
  if (soldDays != null) return String(Math.round(soldDays * 100) / 100);
  return filled(l.jours_max) ? String(l.jours_max) : text(l.jours_min);
}

/* Brouillon de phase projet construit à partir d'une ligne vendue d'une affaire. */
export function lineToPhaseDraft(l) {
  // Forfait échelonné : côté projet, un forfait global (budget total) ; ses dates de paiement ne
  // sont pas celles du chantier, la fin de la phase se saisit à la conversion.
  if (l.facturation === INSTALLMENT_BILLING)
    return lineToPhaseDraft({
      ...l,
      facturation: 'forfait_global',
      date_fin_prevue: '',
      // Pas de jours sur la fiche d'un échelonné : un reste d'un ancien mode ne doit pas compter.
      jours_vendus: '',
      unite_vendu: 'jours',
    });
  const factL = l.facturation || 'forfait_brique';
  const isRec = RECURRING_BILLING.includes(factL);
  const montant = filled(l.budget_vendu) ? String(l.budget_vendu) : lineBudgetMax(l) || '';
  // Régie : le "vendu" (TJM négocié, nb de devs signés) est porté par des champs dédiés
  // (tjm_vendu, nb_devs_vendu), distincts de l'estimé (tjm, nb_devs_estime) — c'est ce vendu
  // qui doit alimenter la phase projet, pas l'estimé.
  const isTimeAndMaterials = factL === 'regie_temporelle';
  // Régie horaire : la phase garde son calcul en jours (TJM, nb de devs). Elle reçoit le taux
  // horaire vendu ramené à la journée, et les heures vendues par mois en équivalent devs.
  const hourly = factL === 'regie_horaire' && {
    tjm: filled(l.taux_horaire_vendu)
      ? String(parseFloat(l.taux_horaire_vendu) * HOURS_PER_DAY)
      : '',
    nb_devs: filled(l.heures_vendu)
      ? String(
          Math.round(
            (hourlySoldHoursPerMonth(l) / (HOURS_PER_DAY * WORKING_DAYS_PER_MONTH)) * 1000,
          ) / 1000,
        )
      : '',
  };
  return {
    _key: uid(), // clé stable pour React (pas encore de vraie phase en base à ce stade du formulaire)
    ligne_id: l.id,
    nom: l.nom || '',
    facturation: factL,
    // Forfait : le TJM vendu prime sur le TJM estimé.
    tjm: hourly
      ? hourly.tjm
      : isTimeAndMaterials || filled(l.tjm_vendu)
        ? text(l.tjm_vendu)
        : text(l.tjm),
    jours_total: joursTotal(l, factL),
    montant_global: String((isRec ? (l.montant_mensuel ?? '') : montant) || ''),
    date_debut: l.date_debut_prevue || '',
    date_fin: l.date_fin_prevue || '',
    jira_project_key: '',
    nb_devs: hourly ? hourly.nb_devs : isTimeAndMaterials ? text(l.nb_devs_vendu) : '',
    // Durée : les dates priment (comme au CRM), sinon l'engagement, sinon 12 mois si seul le
    // début est connu. Engagement et dates concordent de toute façon (écart bloqué au CRM).
    duree_mois: String(
      contractTotalMonths(l.date_debut_prevue, l.date_fin_prevue) ||
        (filled(l.engagement_mois_vendu)
          ? l.engagement_mois_vendu
          : l.date_debut_prevue && !l.date_fin_prevue
            ? 12
            : ''),
    ),
    correctif_qte: text(l.correctif_qte),
    correctif_unite: l.correctif_unite || 'heures_mois',
    evolutif_qte: text(l.evolutif_qte),
    evolutif_unite: l.evolutif_unite || 'jours_an',
  };
}
